import { useState, useEffect } from "react";
import MainLayout from "@/layouts/MainLayout";
import { toast } from "sonner"
import { useNavigate } from "react-router-dom";
import { Check, FileText, X } from "lucide-react";
import { v4 as uuidv4 } from "uuid";
import { Button } from "@/components/ui/button.tsx";
import { Card, CardHeader, CardContent, CardFooter } from "@/components/ui/card.tsx";
import { Checkbox } from "@/components/ui/checkbox.tsx";
import { Label } from "@/components/ui/label.tsx";
import AuthService from "@/lib/auth";
import { useLocation } from "@/contexts/LocationContext";
import { getCurrentUser } from "aws-amplify/auth";

export default function HIPPAConsent() {
  const navigate = useNavigate();
  const { selectedLocationId } = useLocation();
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const user = await getCurrentUser();
        const profile = await AuthService.getProfile(user.username);
        setCurrentUser(profile);
      } catch (err) {
        console.warn("HIPPAConsent: Failed to fetch current user profile", err);
      }
    };
    fetchUser();
  }, []);
  const [consents, setConsents] = useState({
    email: false,
    biometric: false,
    e_sign: false,
  });

  const handleConsentChange = (name: keyof typeof consents) => {
    setConsents(prev => ({
      ...prev,
      [name]: !prev[name]
    }));
  };

  const isFormValid = () => {
    return consents.email && consents.e_sign;
  };

  const handleHipaaConsent = async (accepted: boolean) => {
    setLoading(true);
    try {
      if (accepted) {
        // 1. Create the patient record (RDS via API)
        const patientId = uuidv4();

        // Prepare consent logs for the initial creation as per working CURL
        const initialConsentLogs = [
          {
            type: 'hipaa_consent',
            uuid: patientId,
            status: 'accepted',
            details: 'Patient accepted HIPAA consent form',
            timestamp: uuidv4() // CURL uses a UUID for timestamp
          },
          {
            type: 'sms_consent',
            uuid: patientId,
            status: 'accepted',
            details: 'Patient consented to receive SMS notifications',
            timestamp: uuidv4()
          },
          {
            type: 'email_consent',
            uuid: patientId,
            status: consents.email ? 'accepted' : 'declined',
            details: consents.email ? 'Patient consented to email communications' : 'Patient declined email communications',
            timestamp: uuidv4()
          },
          {
            type: 'e_sign_consent',
            uuid: patientId,
            status: consents.e_sign ? 'accepted' : 'declined',
            details: consents.e_sign ? 'Patient consented to e-sign communications' : 'Patient declined e-sign communications',
            timestamp: uuidv4()
          },
          {
            type: 'biometric_consent',
            uuid: patientId,
            status: consents.biometric ? 'accepted' : 'declined',
            details: consents.biometric ? 'Patient consented to biometric data processing' : 'Patient declined biometric data processing',
            timestamp: uuidv4()
          }
        ];

        const patientData = {
          auth_uuid: patientId, // Required as per working CURL
          first_name: "Registration",
          last_name: "Pending",
          mobile_number: "0000000000",
          email: null,
          gender: "Male", // Aligning with CURL sample
          date_of_birth: "1900-01-01",
          receive_sms: true,
          selected_tests: [],
          location_ids: selectedLocationId ? [Number(selectedLocationId)] : [], 
          consent_logs: initialConsentLogs,
          enrolled_for_monitorhealth_services: true,
        };

        console.log('🔄 HIPPAConsent: Creating patient with data:', patientData);
        const patient = await AuthService.createPatient(patientData) as any;
        console.log('✅ HIPPAConsent: Patient created response:', patient);

        // Robust extraction: backend might return a single object or an array
        const finalPatientId = (Array.isArray(patient) ? patient[0]?.id : patient?.id) || patientId;

        // 2. Create patient encounter (RDS via API)
        const encounterIdStr = `ENC-${Date.now()}-${finalPatientId.substring(0, 8)}`;
        const now = new Date();
        const startTime = now.toISOString().replace('T', ' ').split('.')[0] + '+00';
        const endTime = new Date(now.getTime() + 30 * 60000).toISOString().replace('T', ' ').split('.')[0] + '+00';

        const encounterData = {
          patient_id: finalPatientId,
          primary_provider_id: currentUser?.id || "277123c8-e92d-448d-b6d3-3ff85b0261b1",
          secondary_provider_id: null,
          facility_id: selectedLocationId ? Number(selectedLocationId) : null,
          department: "General",
          encounter_date: now.toISOString().split("T")[0],
          encounter_type: "InPerson",
          status: "active",
          start_time: startTime,
          end_time: endTime,
          visit_reason: "Initial Registration / HIPAA Consent",
          visit_priority: "routine",
          chief_complaint: "Registration",
          clinical_notes: "Patient completed initial registration and HIPAA consent",
          encounter_summary: null,
          follow_up_instructions: null,
          encounter_review_status: "pending",
          location_ids: selectedLocationId ? [Number(selectedLocationId)] : [],
        };
        console.log('🔄 HIPPAConsent: Creating encounter with data:', encounterData);
        const encounter = await AuthService.createPatientEncounter(encounterData) as any;
        console.log('✅ HIPPAConsent: Encounter created response:', encounter);

        // Handle potential array response for encounter too
        const finalEncounterId = (Array.isArray(encounter) ? encounter[0]?.encounter_id : encounter?.encounter_id) || encounterIdStr;

        // 3. Optional: Sync any remaining state (though creation should have covered it)
        try {
          const existingPatient = await AuthService.getPatient(finalPatientId) as { consent_logs?: any[] } | null;
          const existingLogs = Array.isArray(existingPatient?.consent_logs) ? existingPatient.consent_logs.filter(Boolean) : [];

          // If creation didn't manage to store logs, we can try update as fallback
          if (existingLogs.length === 0) {
            await AuthService.updatePatient(finalPatientId, {
              consent_logs: initialConsentLogs,
            });
          }
        } catch (syncErr) {
          console.warn("HIPPAConsent: Optional patient sync/update failed", syncErr);
        }

        navigate(`/patients/${finalPatientId}/${finalEncounterId}/create`);
      } else {
        toast.error("HIPAA consent is required to proceed");
      }
    } catch (error) {
      console.error('Error processing HIPAA consent:', error);
      toast.error("Error saving consent. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout>
      <div className="w-full sm:px-6">
        <Card className="rounded-2xl shadow-lg shadow-green-200/40 border border-gray-200">
          <CardHeader className="border-b px-6 pt-6 pb-4">
            <div className="mb-4">
              <div className="flex items-center">
                <FileText className="mr-3 text-green-600" size={24} />
                <h2 className="text-xl sm:text-2xl font-bold text-gray-800">HIPAA Privacy Notice</h2>
              </div>

              <p>In case this form is being filled on behalf of patient , please ask the patients for consent</p>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div style={{
                width: "100%", // Adjust dynamically if needed
                background: "linear-gradient(to right, #34d399, #facc15, #d1fae5)", // tailwind: from-green-400 via-yellow-400 to-green-100
              }} className="bg-blue-600 h-2 rounded-full w-2/4"></div>
            </div>
          </CardHeader>

          <CardContent className="p-6 max-h-100 overflow-y-auto text-sm text-gray-700 space-y-4">
            <p><strong>This notice describes how medical information about you may be used and disclosed and how you can get access to this information. Please review it carefully.</strong></p>

            <div>
              <h4 className="font-semibold mb-1">Uses and Disclosures of Protected Health Information</h4>
              <p>We may use and disclose your protected health information (PHI) for the following purposes:</p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li><strong>Treatment:</strong> Coordinating and managing your healthcare.</li>
                <li><strong>Payment:</strong> To receive payment for services.</li>
                <li><strong>Healthcare Operations:</strong> For internal healthcare management.</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-1">Your Rights Regarding Your PHI</h4>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>Request restrictions on disclosures</li>
                <li>Receive confidential communications</li>
                <li>Inspect and copy your PHI</li>
                <li>Request amendments</li>
                <li>Get an accounting of disclosures</li>
                <li>Request a paper copy of this notice</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-1">Complaints</h4>
              <p>If you believe your privacy rights have been violated, file a complaint with us or the Department of Health and Human Services.</p>
            </div>

            <p className="text-xs text-gray-500 mt-2">Effective as of January 1, 2024.</p>
          </CardContent>

          <CardFooter className="p-6 border-t">
            <div className="space-y-4 w-full">
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="email-consent"
                  checked={consents.email}
                  onCheckedChange={() => handleConsentChange('email')}
                  className="border-2 border-green-500 rounded hover:border-green-600 focus:ring-2 focus:ring-green-200 focus:ring-offset-2"
                />
                <div className="grid gap-1.5 leading-none">
                  <Label htmlFor="email-consent" className="text-sm font-medium leading-none">
                    I consent to receive email & sms communications
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    We'll send you important updates and notifications about your account.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <Checkbox
                  id="biometric-consent"
                  checked={consents.biometric}
                  onCheckedChange={() => handleConsentChange('biometric')}
                  className="border-2 border-green-500 rounded hover:border-green-600 focus:ring-2 focus:ring-green-200 focus:ring-offset-2"
                />
                <div className="grid gap-1.5 leading-none">
                  <Label htmlFor="biometric-consent" className="text-sm font-medium leading-none">
                    I consent to facial recognition for identity verification <span className="text-xs text-muted-foreground font-normal">(optional)</span>
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Your facial biometric data will be processed by AWS Rekognition. No images or videos will be stored.
                  </p>
                </div>
              </div>

              {/*Add one more consent checkbox here for e-sign to be captured and stored in the database*/}
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="e-sign-consent"
                  checked={consents.e_sign}
                  onCheckedChange={() => handleConsentChange('e_sign')}
                  className="border-2 border-green-500 rounded hover:border-green-600 focus:ring-2 focus:ring-green-200 focus:ring-offset-2"
                />
                <div className="grid gap-1.5 leading-none">
                  <Label htmlFor="e-sign-consent" className="text-sm font-medium leading-none">
                    I consent to e-sign
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Your signature will be captured as part of this form.
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Button
                  onClick={() => handleHipaaConsent(false)}
                  disabled={loading}
                  variant="destructive"
                  className="w-full sm:w-1/2 text-white font-semibold h-[56px] text-base rounded-full bg-gradient-to-r from-red-400 to-red-500 hover:scale-[1.02] transition-transform shadow-lg shadow-green-300/40"
                >
                  {loading ? (
                    <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <X className="mr-2" size={16} />
                      Decline
                    </>
                  )}
                </Button>
                <Button
                  onClick={() => handleHipaaConsent(true)}
                  disabled={!isFormValid() || loading}
                  className="w-full sm:w-1/2 text-white font-semibold h-[56px] text-base rounded-full bg-gradient-to-r from-green-400 to-green-500 hover:scale-[1.02] transition-transform shadow-lg shadow-green-300/40 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Check className="mr-2" size={18} />
                      Accept & Continue
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardFooter>
        </Card>
      </div>
    </MainLayout>
  );
}